import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiRequestService } from '../../shared/api-request.service';
import { AppService } from '../../app.service';

/**
 * Holds the signed-in user's OWN membership requests (SC-301).
 *
 * Distinct from AccessRequestsService, which holds the count of requests an ACCOUNT has to review. This is
 * the other side of the same workflow: what the person who asked to join is waiting on.
 *
 * Held in one place because two surfaces depend on it and they must not disagree: the shell banner, which
 * appears while a request is pending, and the Account Access entry in the account menu, which is the only
 * route back to the status page once the user navigates away from it.
 */
@Injectable({
  providedIn: 'root'
})
export class UserAccessRequestsService {
  private requestsSubject = new BehaviorSubject<any[]>([]);

  /**
   * Every outstanding or refused request. The endpoint returns pending and declined rows only, so an
   * approved membership never appears here.
   */
  public requests: Observable<any[]> = this.requestsSubject.asObservable();

  constructor(
    private api: ApiRequestService,
    private app: AppService
  ) {}

  /**
   * Refreshes the user's own requests.
   *
   * Silently does nothing when unauthenticated, so it is safe to call from shared entry points such as the
   * permissions-changed signal without every caller repeating the check.
   *
   * @return {void}
   */
  refresh(): void {
    if ( !this.app.isAuthenticated ) {
      this.requestsSubject.next([]);
      return;
    }

    this.api.makeRequest('get', 'v2/user/account-access-requests')
      .then((response: any): void => {
        this.requestsSubject.next(response?.data || []);
      })
      .catch((): void => {
        // These surfaces are informational. A failed fetch must not surface an error on every page load.
        this.requestsSubject.next([]);
      });
  }

  /**
   * Clears the state, for sign out and account switch, so one user's requests cannot bleed into the next.
   *
   * @return {void}
   */
  clear(): void {
    this.requestsSubject.next([]);
  }

  /**
   * The current request list, for callers that need it synchronously rather than through the observable.
   *
   * @return {any[]} The outstanding and refused requests.
   */
  current(): any[] {
    return this.requestsSubject.getValue();
  }
}
